import { PORTAL_URL } from '../lib/config.js';

const stats = [
  {
    figure: '2h',
    unit: 'al día',
    label: 'que dejas de teclear',
    note: 'Tiempo recuperado por médico de tiempo completo en consulta externa.',
  },
  {
    figure: '80',
    unit: '% menos',
    label: 'de minutos por historia',
    note: 'De 9 minutos promedio a menos de 2, según pilotos en consultorios LATAM.',
  },
  {
    figure: '100',
    unit: '% mirando',
    label: 'al paciente',
    note: 'Sin pantalla intermediando. La consulta vuelve a ser conversación.',
  },
  {
    figure: '0',
    unit: 'plantillas',
    label: 'que tengas que mantener',
    note: 'Anamnia aprende tu estructura. Tú firmas; ella adapta el formato.',
  },
];

const testimonials = [
  {
    quote:
      'Llegué a las 7 a.m. y me fui a las 4. Sin notas pendientes. Llevaba años sin tener una tarde libre.',
    name: 'Dra. ███████ ████',
    role: 'Medicina Interna · Consultorio independiente',
    city: 'Bogotá, CO',
  },
  {
    quote:
      'Mis pacientes notaron antes que yo. Una me dijo: «doctor, hoy sí me miró».',
    name: 'Dr. ██████ █████',
    role: 'Medicina Familiar · Centro médico',
    city: 'Medellín, CO',
  },
  {
    quote:
      'No es transcripción. Es una historia clínica que entiende lo que es relevante y lo que no.',
    name: 'Dra. █████ ███████',
    role: 'Pediatría · Consultorio compartido',
    city: 'Cali, CO',
  },
];

type SpokenLine = { speaker: 'M' | 'P'; text: string; delay: number };

const transcriptLines: SpokenLine[] = [
  { speaker: 'P', text: 'Doctora, llevo como tres semanas con dolor en la espalda baja. Empeora cuando manejo.', delay: 320 },
  { speaker: 'M', text: '¿Le baja a la pierna? ¿Le da hormigueo en algún lado?', delay: 720 },
  { speaker: 'P', text: 'Sí, hasta la pantorrilla derecha. Y me cuesta dormir de ese lado.', delay: 1100 },
];

export default function Home() {
  return (
    <main className="overflow-hidden text-[color:var(--color-ink)]">
      <Hero />
      <Demo />
      <Stats />
      <Testimonials />
      <FinalCta />
      <SiteFooter />
    </main>
  );
}

function Hero() {
  return (
    <section className="relative isolate">
      <div className="anamnia-grain" aria-hidden />
      <div className="absolute inset-0 -z-10" aria-hidden>
        <div className="absolute -left-32 top-24 h-[28rem] w-[28rem] rounded-full bg-brand-100/60 blur-3xl" />
        <div className="absolute right-[-10rem] top-72 h-[22rem] w-[22rem] rounded-full bg-accent-100/60 blur-3xl" />
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-x-10 gap-y-14 px-6 pb-24 pt-16 md:grid-cols-12 md:pt-24 lg:px-10">
        {/* Eyebrow column — magazine style */}
        <aside className="md:col-span-3 md:pt-3">
          <div className="anamnia-rise flex items-baseline gap-3 [animation-delay:60ms]">
            <span className="block h-px w-10 bg-accent-500" aria-hidden />
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-700">
              Vol. 01 · Anamnia Voz
            </p>
          </div>
          <p className="anamnia-rise mt-6 max-w-xs font-serif text-base italic leading-relaxed text-neutral-600 [animation-delay:160ms]">
            Una conversación clínica produce, en promedio, 800 palabras. Sólo 90 sobreviven al
            teclado.
          </p>
          <p className="anamnia-rise mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-500 [animation-delay:220ms]">
            Fuente — Tiempo dedicado a documentación clínica, JAMA Intern Med, 2024.
          </p>
        </aside>

        {/* Headline column */}
        <div className="md:col-span-9">
          <h1 className="anamnia-rise font-serif text-5xl font-semibold leading-[0.98] tracking-[-0.02em] text-brand-900 sm:text-6xl md:text-[5.5rem] [animation-delay:120ms]">
            {'La consulta vuelve '}
            <br className="hidden sm:inline" />
            {'a ser '}
            <span className="relative inline-block">
              <span className="relative z-10">conversación</span>
              <UnderlineMark className="absolute -bottom-2 left-0 w-full text-accent-500" />
            </span>
            <span className="text-accent-500">.</span>
          </h1>

          <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-10 md:grid-cols-12">
            <p className="anamnia-rise md:col-span-7 max-w-xl text-lg leading-relaxed text-neutral-700 [animation-delay:260ms]">
              Anamnia escucha la consulta, escribe la historia clínica, y te devuelve la mirada
              al paciente. Diseñada con médicos en Colombia para consultorios pequeños, centros
              médicos y profesionales independientes en LATAM.
            </p>

            <div className="anamnia-rise md:col-span-5 [animation-delay:340ms]">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                <a
                  href={`${PORTAL_URL}/signup`}
                  className="group relative inline-flex items-center gap-3 rounded-full bg-brand-700 px-7 py-4 text-base font-medium text-paper transition hover:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
                >
                  <span>Solicita acceso anticipado</span>
                  <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  <span
                    className="pointer-events-none absolute -inset-1 rounded-full border border-brand-700/30"
                    aria-hidden
                  />
                </a>
                <a
                  href="#demo"
                  className="inline-flex items-center gap-2 rounded-full border border-brand-900/15 px-5 py-4 text-base text-brand-900 transition hover:border-brand-900/40"
                >
                  <PlayDot />
                  Ver Anamnia escuchando
                </a>
              </div>
              <p className="anamnia-handwriting mt-5 max-w-[20rem] text-base leading-snug">
                — Acceso por invitación durante la fase piloto. Estamos eligiendo cuidado a
                cuidado quién entra primero.
              </p>
            </div>
          </div>
        </div>

        {/* Hero figure — full width, drops below */}
        <figure className="anamnia-rise md:col-span-12 [animation-delay:480ms]">
          <HeroTranscript />
          <figcaption className="mt-4 flex flex-wrap items-center justify-between gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">
            <span>Fig. 01 · Captura en vivo de una consulta · 11:42 a.m.</span>
            <span>Anamnia Voz — sesión #00417</span>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

function HeroTranscript() {
  return (
    <div className="relative overflow-hidden rounded-[1.75rem] border border-brand-900/10 bg-paper-deep/60 shadow-[0_30px_80px_-40px_rgba(15,54,62,0.45)] backdrop-blur">
      <div className="absolute inset-0" aria-hidden>
        <div className="absolute inset-0 [background:radial-gradient(circle_at_12%_18%,rgba(40,121,136,0.18),transparent_45%),radial-gradient(circle_at_88%_82%,rgba(180,96,40,0.16),transparent_45%)]" />
      </div>

      <div className="relative grid grid-cols-1 gap-px bg-brand-900/10 md:grid-cols-12">
        {/* Left: Voice */}
        <div className="bg-paper-deep/90 p-6 md:col-span-5 md:p-9">
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-700">
              <span className="relative inline-flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-[anamnia-pulse-ring_1.6s_ease-out_infinite] rounded-full bg-accent-400" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent-500" />
              </span>
              Escuchando
            </div>
            <span className="font-mono text-[11px] tracking-[0.16em] text-neutral-500">
              ES-CO · 48 kHz
            </span>
          </header>

          <Waveform className="mt-6" />

          <ul className="mt-7 space-y-5">
            {transcriptLines.map((line) => (
              <li
                key={line.delay}
                className="anamnia-rise-soft flex gap-3"
                style={{ animationDelay: `${line.delay}ms` }}
              >
                <SpeakerBadge speaker={line.speaker} />
                <p className="text-[15px] leading-relaxed text-neutral-800">{line.text}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: Generated clinical record */}
        <div className="relative bg-paper p-6 md:col-span-7 md:p-9">
          <header className="flex items-center justify-between border-b border-brand-900/10 pb-3">
            <p className="font-serif text-sm font-semibold uppercase tracking-[0.25em] text-brand-900">
              Historia clínica · borrador
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-500">
              Auto-guardado
            </p>
          </header>

          <div className="anamnia-rule mt-5 space-y-4 pt-1 font-serif text-[15px] leading-[1.7] text-brand-900">
            <p>
              <span className="font-semibold">Motivo de consulta.</span>{' '}
              <span className="anamnia-rise-soft inline [animation-delay:1450ms]">
                Lumbalgia de tres semanas de evolución con irradiación a miembro inferior derecho.
              </span>
            </p>
            <p>
              <span className="font-semibold">Padecimiento actual.</span>{' '}
              <span className="anamnia-rise-soft inline [animation-delay:1750ms]">
                Dolor lumbar mecánico que aumenta con la sedestación prolongada (manejo
                vehicular). Irradia a pantorrilla derecha. Interfiere con descanso nocturno.
              </span>
              <span className="anamnia-rise-soft inline [animation-delay:2050ms] text-brand-700">
                {' '}
                Niega trauma, fiebre o pérdida de peso.
              </span>
              <span
                className="ml-1 inline-block h-[1.05em] w-[2px] translate-y-[3px] bg-accent-500 animate-[anamnia-blink_900ms_steps(1)_infinite]"
                aria-hidden
              />
            </p>
          </div>

          {/* Margin annotation */}
          <div className="mt-7 border-t border-dashed border-brand-900/15 pt-4">
            <p className="anamnia-handwriting text-base leading-snug">
              <span className="mr-2 inline-block rotate-[-4deg]">↳</span>
              Sugerencia: agregar test de Lasègue al examen físico — patrón compatible con
              radiculopatía L5-S1.
            </p>
            <div className="mt-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-500">
              <span>Tú firmas. Anamnia se calla.</span>
              <span className="h-px flex-1 bg-brand-900/15" />
              <span className="text-brand-700">v3.4</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Demo() {
  return (
    <section
      id="demo"
      className="relative border-y border-brand-900/10 bg-brand-900 py-24 text-paper md:py-32"
    >
      <div className="anamnia-grain opacity-30" aria-hidden />
      <div className="absolute inset-0 -z-0 opacity-20" aria-hidden>
        <div className="h-full w-full [background:radial-gradient(circle_at_30%_20%,rgba(212,127,75,0.4),transparent_50%),radial-gradient(circle_at_75%_70%,rgba(40,121,136,0.6),transparent_50%)]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent-300">
              Capítulo 02 — Demostración
            </p>
            <h2 className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-[-0.01em] sm:text-5xl">
              {'Tres minutos. '}
              <br />
              <span className="italic text-accent-300">Una consulta entera.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-paper/80">
              Mira a Anamnia transcribiendo una consulta real, organizándola por secciones de
              historia clínica y dejándola lista para que el médico solo revise y firme.
            </p>

            <dl className="mt-10 space-y-4 font-mono text-[12px] uppercase tracking-[0.16em] text-paper/70">
              <DemoMeta label="Duración" value="3 min 14 s" />
              <DemoMeta label="Idioma" value="Español · Colombia" />
              <DemoMeta label="Especialidad" value="Medicina Interna" />
              <DemoMeta label="Sin edición posterior" value="Sí" />
            </dl>
          </div>

          <div className="md:col-span-7">
            <div className="group relative aspect-video overflow-hidden rounded-2xl border border-paper/15 bg-brand-900 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]">
              <div className="absolute inset-0" aria-hidden>
                <div className="absolute inset-0 [background:linear-gradient(135deg,rgba(40,121,136,0.55),rgba(15,54,62,0.92))]" />
                <svg className="absolute inset-0 h-full w-full" viewBox="0 0 800 450" aria-hidden>
                  <defs>
                    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(246,240,230,0.08)" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect width="800" height="450" fill="url(#grid)" />
                  <path
                    d="M 0 280 Q 100 200 200 260 T 400 240 T 600 270 T 800 230"
                    fill="none"
                    stroke="rgba(212,127,75,0.6)"
                    strokeWidth="2"
                  />
                  <path
                    d="M 0 300 Q 120 230 240 290 T 480 270 T 720 280 T 800 260"
                    fill="none"
                    stroke="rgba(158,201,210,0.45)"
                    strokeWidth="1.5"
                  />
                </svg>
              </div>

              <div className="relative flex h-full flex-col items-center justify-center text-center">
                <button
                  type="button"
                  className="group/play flex h-20 w-20 items-center justify-center rounded-full border border-paper/30 bg-paper/10 text-paper backdrop-blur transition hover:scale-105 hover:bg-paper/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
                  aria-label="Reproducir demostración (próximamente)"
                  disabled
                >
                  <PlayIcon className="h-7 w-7 translate-x-0.5" />
                  <span
                    className="pointer-events-none absolute h-20 w-20 animate-[anamnia-pulse-ring_2.4s_ease-out_infinite] rounded-full border border-paper/40"
                    aria-hidden
                  />
                </button>
                <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-paper/70">
                  Video disponible en el lanzamiento — Q3 2026
                </p>
              </div>

              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between border-t border-paper/10 bg-brand-900/80 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-paper/70">
                <span>00:00 / 03:14</span>
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
                  Próximamente
                </span>
              </div>
            </div>

            <p className="mt-4 font-serif italic leading-snug text-paper/70">
              Fig. 02 — Demostración grabada con consentimiento informado.
              Datos de paciente alterados por privacidad.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DemoMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-4 border-b border-paper/15 pb-3">
      <dt className="w-32 shrink-0 text-paper/60">{label}</dt>
      <dd className="flex-1 text-paper">{value}</dd>
    </div>
  );
}

function Stats() {
  return (
    <section className="relative bg-paper py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent-600">
              Capítulo 03 — Lo que cambia
            </p>
            <h2 className="mt-4 font-serif text-4xl font-semibold leading-[1.05] tracking-[-0.01em] text-brand-900 sm:text-5xl">
              {'Cuatro números '}
              <br />
              <span className="italic text-brand-700">que tu calendario notará.</span>
            </h2>
          </div>
          <p className="anamnia-handwriting max-w-xs text-base leading-snug">
            — Calculados sobre pilotos en Colombia, 2026. Conservadores: el promedio fue
            mejor.
          </p>
        </div>

        <ol className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-3xl bg-brand-900/10 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, i) => (
            <li
              key={stat.label}
              className="group relative bg-paper p-7 transition-colors hover:bg-paper-deep/70 md:p-9"
            >
              <span className="absolute right-7 top-7 font-mono text-[11px] tracking-[0.18em] text-neutral-400">
                0{i + 1}
              </span>
              <p className="font-serif text-[5.5rem] leading-none font-semibold tracking-[-0.04em] text-brand-900 anamnia-tabular md:text-[6.5rem]">
                {stat.figure}
              </p>
              <p className="mt-1 font-serif text-xl italic text-accent-600">{stat.unit}</p>
              <p className="mt-6 font-serif text-lg leading-tight text-brand-900">
                {stat.label}
              </p>
              <p className="mt-3 max-w-[18rem] text-sm leading-relaxed text-neutral-600">
                {stat.note}
              </p>
              <span
                className="absolute bottom-0 left-7 right-7 h-px origin-left scale-x-0 bg-accent-500 transition-transform duration-700 group-hover:scale-x-100"
                aria-hidden
              />
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section id="voces" className="relative bg-paper-deep/40 py-24 md:py-32">
      <div className="anamnia-grain" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand-700">
              Capítulo 04 — Voces piloto
            </p>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl font-semibold leading-[1.05] tracking-[-0.01em] text-brand-900 sm:text-5xl">
              {'Médicas y médicos '}
              <br />
              <span className="italic">en consultorios reales.</span>
            </h2>
          </div>
          <p className="hidden max-w-[14rem] font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500 md:block">
            Identidades reservadas durante la fase piloto.
            <br />
            Citas autorizadas, datos editados.
          </p>
        </div>

        <ul className="mt-16 grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-3">
          {testimonials.map((t, i) => (
            <li key={t.name} className="relative flex flex-col">
              <span
                aria-hidden
                className="font-serif text-[10rem] leading-none text-accent-300/70 [text-shadow:0_2px_0_rgba(15,54,62,0.05)]"
              >
                “
              </span>
              <blockquote className="-mt-14 font-serif text-xl leading-relaxed text-brand-900">
                {t.quote}
              </blockquote>
              <div className="mt-6 flex items-center gap-3">
                <Avatar index={i} />
                <div>
                  <p className="font-serif text-base font-semibold text-brand-900">{t.name}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">
                    {t.role}
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-accent-600">
                    {t.city}
                  </p>
                </div>
              </div>
              <div className="mt-8 h-px w-12 bg-brand-900/30" aria-hidden />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative bg-paper py-24 md:py-32">
      <div className="mx-auto max-w-5xl px-6 text-center lg:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent-600">
          Capítulo final
        </p>
        <h2 className="mt-6 font-serif text-5xl font-semibold leading-[1.02] tracking-[-0.02em] text-brand-900 sm:text-6xl md:text-7xl">
          {'Tu próxima consulta '}
          <br />
          <span className="italic text-brand-700">puede ser distinta.</span>
        </h2>
        <p className="mx-auto mt-8 max-w-xl text-lg leading-relaxed text-neutral-700">
          Únete al grupo de fundadores. Estamos abriendo cupos por especialidad y región para
          los primeros 100 médicos en Colombia. Sin tarjeta, sin contrato, hasta que firmes.
        </p>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
          <a
            href={`${PORTAL_URL}/signup`}
            className="group relative inline-flex items-center gap-3 rounded-full bg-accent-500 px-8 py-4 text-base font-semibold text-paper transition hover:bg-accent-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
          >
            Solicita tu cupo de fundador
            <Arrow className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href={`${PORTAL_URL}/login`}
            className="inline-flex items-center gap-2 text-base font-medium text-brand-700 underline decoration-accent-400 decoration-2 underline-offset-[6px] hover:text-brand-900 hover:decoration-accent-500"
          >
            Ya tengo cuenta
          </a>
        </div>

        <p className="mt-10 inline-flex items-center gap-2 rounded-full border border-brand-900/10 bg-paper-deep/70 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-600">
          <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
          Datos clínicos cifrados · cumplimos con habeas data Ley 1581 / Col
        </p>
      </div>
    </section>
  );
}

function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative border-t border-brand-900/10 bg-brand-900 text-paper">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-14 md:grid-cols-12 lg:px-10">
        <div className="md:col-span-5">
          <p className="font-serif text-3xl font-semibold tracking-[-0.01em]">
            anamnia<span className="text-accent-400">.</span>
          </p>
          <p className="mt-4 max-w-sm font-serif italic text-paper/70">
            Donde empieza el cuidado.
          </p>
        </div>
        <nav className="md:col-span-4 md:col-start-7">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-paper/50">
            Producto
          </p>
          <ul className="mt-4 space-y-2 font-serif text-paper/90">
            <li>
              <a href="#demo" className="hover:text-accent-300">
                Anamnia Voz
              </a>
            </li>
            <li className="text-paper/50">Anamnia Triage · próximamente</li>
            <li className="text-paper/50">Anamnia Agenda · próximamente</li>
          </ul>
        </nav>
        <div className="md:col-span-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-paper/50">
            Compañía
          </p>
          <p className="mt-4 font-serif text-paper/90">
            KANUMA S.A.S.
            <br />
            Bogotá D.C., Colombia
            <br />
            <span className="font-mono text-[12px] tracking-wider text-paper/60">
              NIT 902.058.342-6
            </span>
          </p>
        </div>
      </div>
      <div className="border-t border-paper/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-6 py-5 font-mono text-[11px] uppercase tracking-[0.18em] text-paper/50 md:flex-row md:items-center md:justify-between lg:px-10">
          <p>© {year} Anamnia · Todos los derechos reservados</p>
          <p>Hecho con cuidado clínico en LATAM.</p>
        </div>
      </div>
    </footer>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Decorative atoms
 * ──────────────────────────────────────────────────────────────────── */

function Waveform({ className = '' }: { className?: string }) {
  const heights = [0.45, 0.7, 0.4, 0.85, 0.55, 0.95, 0.6, 0.78, 0.45, 0.66, 0.92, 0.5, 0.7, 0.4, 0.62];
  return (
    <div
      data-anamnia-wave
      className={`flex h-12 items-end gap-[3px] ${className}`}
      aria-hidden
    >
      {heights.map((h, i) => (
        <span
          key={i}
          className="block w-[6px] origin-bottom rounded-full bg-accent-500/80"
          style={{
            height: `${h * 100}%`,
            animation: `anamnia-wave ${900 + (i % 4) * 220}ms ease-in-out ${i * 70}ms infinite`,
          }}
        />
      ))}
    </div>
  );
}

function SpeakerBadge({ speaker }: { speaker: 'M' | 'P' }) {
  const isDoctor = speaker === 'M';
  return (
    <span
      className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-medium ${
        isDoctor
          ? 'bg-brand-700 text-paper'
          : 'bg-paper text-brand-800 ring-1 ring-brand-900/20'
      }`}
      aria-label={isDoctor ? 'Médica' : 'Paciente'}
    >
      {speaker}
    </span>
  );
}

function Avatar({ index }: { index: number }) {
  const palettes = [
    { bg: 'var(--color-brand-100)', fg: 'var(--color-brand-800)' },
    { bg: 'var(--color-accent-100)', fg: 'var(--color-accent-700)' },
    { bg: 'var(--color-brand-700)', fg: 'var(--color-paper)' },
  ];
  const p = palettes[index % palettes.length] ?? palettes[0]!;
  return (
    <span
      className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-serif text-lg font-semibold"
      style={{ background: p.bg, color: p.fg }}
      aria-hidden
    >
      <svg viewBox="0 0 32 32" className="h-7 w-7">
        <circle cx="16" cy="12" r="5" fill="currentColor" opacity="0.85" />
        <path d="M5 28c1.6-5 6-7 11-7s9.4 2 11 7" fill="currentColor" opacity="0.6" />
      </svg>
    </span>
  );
}

function UnderlineMark({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 320 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path
        d="M4 14 C 60 4, 140 4, 200 12 S 290 18, 316 8"
        style={{
          strokeDasharray: 320,
          strokeDashoffset: 320,
          animation: 'anamnia-mark 1.4s 600ms ease-out forwards',
        }}
      />
    </svg>
  );
}

function Arrow({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 8h10" />
      <path d="M9 4l4 4-4 4" />
    </svg>
  );
}

function PlayIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M7 4.5v15l13-7.5z" />
    </svg>
  );
}

function PlayDot() {
  return (
    <span className="relative inline-flex h-2.5 w-2.5">
      <span className="absolute inline-flex h-full w-full animate-[anamnia-pulse-ring_2s_ease-out_infinite] rounded-full bg-accent-400/70" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent-500" />
    </span>
  );
}
